import {
  Button,
  Classes,
  Dialog,
  Icon,
  Menu,
  MenuItem,
  Popover,
  PopoverInteractionKind,
} from '@blueprintjs/core';
import { formatDatetime } from '../../../utils/formatDate';
import { useContext, useState } from 'react';
import styles from './Actions.module.css';
import bankStyles from '../BankPanel.module.css';
import {
  event_log,
  loan_application,
  loan_quote,
  loan_request,
  permissions,
  Product,
} from '@axo/shared/data-access/types';
import { AcceptDialog } from './AcceptDialog';
import { ActionsMenuButton } from './ActionsMenuButton';
import { ConfirmAction } from './ConfirmAction';
import {
  lock_hooks,
  useActivateQuote,
  useCancelQuote,
  useCancelRequest,
  useClientEmail,
  useQuoteStatusUtils,
  useRequestStatusUtils,
} from '@axo/shared/data-access/hooks';
import { AppToaster } from '../../AppToaster';
import { Restricted } from '@axo/shared/feature/restricted';
import {
  DataAccessContext,
  useHasAnyOfRoles,
} from '@axo/shared/data-access/provider';
import { getLocalPartOfEmail } from '../../../utils/getLocalPartOfEmail';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { EventCodeACE } from '../../EventLogWrapper/event-code';
import { formatCurrency } from '../../../utils/formatCurrency.ts';
import { useBankPanelStore } from '../../../store/bankPanelStore.ts';
import { AcceptedOfferDialog } from './AcceptedOfferDialog/AcceptedOfferDialog.tsx';
import { IQuoteDetail } from '../hooks/useBankPanelData.tsx';
import { IGroupingStatuses } from '../../../models/tableConfig.ts';
import { classNames } from '../../../utils/classNames.ts';

const bankNorwegianIds = {
  NO_AXO: '57f33df0-267f-4bca-a0a5-108e84cc2f04',
  SE_AXO_ZMARTA: 'd289a695-d324-4922-800d-93116a796b68',
  FI_AXO_ZMARTA: '2a0de0f7-3232-472f-ab07-3027d1ee2196',
};

// TODO: Idea to move hardcoded texts to a separate constant
const tooltipContent = {
  acceptOffer: {
    hasLock: 'You can only accept the offer when you have locked it.',
    isPlaceholderQuote: "You can't accept as no quote has been received.",
    isNotPending:
      'Only applications pending Customer decision can be accepted.',
  },
  editAcceptedOffer: {
    hasLock: 'You must have lock to edit offer.',
    isNotCorrectStatus: 'You can only edit accepted offers.',
  },
  cancelQuote: {
    hasLock: 'You can only cancel the quote when you have locked it.',
    isCanceled: 'The quote is canceled.',
    isDisbursing: 'The quote is disbursing.',
  },
  lenderReference: {
    noReference: 'No Lender Reference has been received.',
  },
  signingLink: {
    noSigningLink: 'The quote does not have a signing link.',
  },
};

type IAction = {
  application: loan_application.LoanApplication;
  quote: IQuoteDetail;
  data: loan_quote.BankQuote[];
  isDisabled?: boolean;
  groupedStatuses: IGroupingStatuses;
};

export function Actions({
  application,
  quote,
  data,
  isDisabled,
  groupedStatuses,
}: IAction) {
  const [isAcceptOpen, setIsAcceptOpen] = useState({ open: false, amount: 0 });
  const [isEditAcceptedOfferOpen, setIsEditAcceptedOfferOpen] = useState({
    open: false,
  });
  const [showConfirmCancelByAxo, setShowConfirmCancelByAxo] = useState(false);
  const [showConfirmCancelRequestByAxo, setShowConfirmCancelRequestByAxo] =
    useState(false);
  const [showConfirmCancelByCustomer, setShowConfirmCancelByCustomer] =
    useState(false);
  const [openMapping, setOpenMapping] = useState<string | null>(null);
  const activate = useActivateQuote(application, quote.ID);
  const cancelRequest = useCancelRequest(
    application,
    quote.LoanRequest.LoanRequestID,
  );
  const { isInQuoteStatusGroup } = useQuoteStatusUtils();
  const { isInRequestStatusGroup } = useRequestStatusUtils();
  const {
    state: { user },
  } = useContext(DataAccessContext);
  const email = useClientEmail(user.clientID);

  const { setIsEditing, setQuoteBeingCreated, setQuoteBeingEditted } =
    useBankPanelStore(application.ID);

  const hasLock = lock_hooks.useCurrentUserHasLock(
    application.ID,
    application.MarketCountry,
  );

  const lenderReference =
    quote.LoanRequest.LenderReference ?? quote.LenderReference ?? '';

  const cancelByAxoQuoteInput = {
    CancelledBy: email.data ? getLocalPartOfEmail(email.data) : '',
    CancelledByBank: false,
  };
  const cancelOnBehalfOfCustomerQuoteInput = {
    ...cancelByAxoQuoteInput,
    OnCustomerBehalf: true,
  };
  const cancelQuoteByAxo = useCancelQuote(
    application.ID,
    application.MarketCountry,
  );
  const cancelQuoteByOnBehalfOfCustomer = useCancelQuote(
    application.ID,
    application.MarketCountry,
  );

  const isCancelled = isInQuoteStatusGroup(
    quote.Status,
    loan_quote.QuoteStatusGroup.Cancelled,
  );
  const isDisbursing = isInQuoteStatusGroup(
    quote.Status,
    loan_quote.QuoteStatusGroup.Disbursing,
  );

  const isApproved = isInQuoteStatusGroup(
    quote.Status,
    loan_quote.QuoteStatusGroup.Approved,
  );

  const userCanEditOffers = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.SecuredSales,
  ]);

  const showEditOffer =
    hasLock.data &&
    ((application.Product === Product.UnsecuredLoan && userCanEditOffers) ||
      (application.Product === Product.SecuredLoan && userCanEditOffers));

  const canEditAcceptedOffer =
    hasLock.data &&
    Object.values(bankNorwegianIds).includes(quote.LenderID) &&
    isInQuoteStatusGroup(quote.Status, loan_quote.QuoteStatusGroup.Accepted);

  const isOfferAcceptDisabled =
    !hasLock.data ||
    quote.IsPlaceholderQuote ||
    !isInQuoteStatusGroup(
      quote.Status,
      loan_quote.QuoteStatusGroup.ApplicationApprovedPendingCustomerDecision,
    );
  const quoteCanBeCancelled =
    hasLock.data && !isCancelled && !isDisbursing && email.data && isApproved;

  const requestCanBeCancelled =
    hasLock.data &&
    quote.IsPlaceholderQuote &&
    isInRequestStatusGroup(
      quote.LoanRequest.Status,
      loan_request.RequestStatusGroup.PendingRegistration,
    );

  function renderAcceptTooltipContent() {
    if (!hasLock.data) return tooltipContent.acceptOffer.hasLock;
    if (quote.IsPlaceholderQuote)
      return tooltipContent.acceptOffer.isPlaceholderQuote;
    return tooltipContent.acceptOffer.isNotPending;
  }

  function renderCancelTooltipContent() {
    if (!hasLock.data) return tooltipContent.cancelQuote.hasLock;
    if (isCancelled) return tooltipContent.cancelQuote.isCanceled;
    if (isDisbursing) return tooltipContent.cancelQuote.isDisbursing;
  }

  function showuldRenderEditAcceptedOffer() {
    return (
      Object.values(bankNorwegianIds).includes(quote.LenderID) &&
      isInQuoteStatusGroup(quote.Status, loan_quote.QuoteStatusGroup.Accepted)
    );
  }

  function renderEditAcceptedOfferTooltipContent() {
    if (!hasLock.data) return tooltipContent.editAcceptedOffer.hasLock;
    if (!showuldRenderEditAcceptedOffer())
      return tooltipContent.editAcceptedOffer.isNotCorrectStatus;
  }

  const handleGoToSignURLClick = (signURL: string) => {
    window.open(signURL, '_blank', 'noopener noreferrer');
  };

  const handleCopySignURLClick = (signURL: string) => {
    navigator.clipboard.writeText(signURL);
    AppToaster.then((toaster) =>
      toaster.show({
        intent: 'primary',
        message: `Signing link ${lenderReference} copied to clipboard`,
      }),
    );
  };

  const handleLenderReferenceClick = (lenderReference: string) => {
    navigator.clipboard.writeText(lenderReference);
    AppToaster.then((toaster) =>
      toaster.show({
        intent: 'primary',
        message: `Lender reference ${lenderReference} copied to clipboard`,
      }),
    );
  };

  const handleEditOffer = (isEdit: boolean) => {
    setIsEditing();
    if (isEdit) {
      setQuoteBeingEditted(quote.ID);
    } else {
      setQuoteBeingCreated(quote.ID);
    }
  };

  const handleEditAcceptedOffer = () => {
    setIsEditAcceptedOfferOpen({
      open: true,
    });
  };

  const appTags: event_log.Resource[] = [
    {
      Type: KnownResourceType.LoanApplication,
      ID: application.ID,
    },
    {
      Type: KnownResourceType.LoanQuote,
      ID: quote.ID,
    },
  ];
  const log = useEventLogger(...appTags);

  // Only render first cell when in a group
  if (
    groupedStatuses?.['LoanQuoteID']?.isInGroup &&
    !groupedStatuses?.['LoanQuoteID']?.isFirstInGroup
  ) {
    return (
      <div
        className={classNames(
          bankStyles.grouped,
          groupedStatuses?.['LoanQuoteID']?.isLastInGroup &&
            bankStyles.lastInGroup,
        )}
      />
    );
  }

  const isActionsButtonDisabled =
    !quote.Lender.BrokerConfiguration?.find(
      (config) => config.Broker === application.Meta?.Broker,
    )?.Active || isDisabled;

  return (
    <div
      className={classNames(
        groupedStatuses?.['LoanQuoteID']?.isInGroup && bankStyles.grouped,
        groupedStatuses?.['LoanQuoteID']?.isFirstInGroup &&
          bankStyles.firstInGroup,
      )}
    >
      <Popover
        disabled={isActionsButtonDisabled}
        interactionKind={PopoverInteractionKind.HOVER}
        onClosed={() => {
          setShowConfirmCancelByAxo(false);
          setShowConfirmCancelByCustomer(false);
        }}
        content={
          <Menu aria-label="Actions menu" role="menu">
            <Restricted
              isAllowedFor={[
                permissions.RoleName.Manager,
                permissions.RoleName.Sales,
                permissions.RoleName.SecuredSales,
                permissions.RoleName.Telemarketing,
              ]}
            >
              <>
                <MenuItem text={'Accept Offer'} icon={'dollar'}>
                  <ActionsMenuButton
                    isDisabled={isOfferAcceptDisabled}
                    icon={'equals'}
                    text={
                      'Accept active offer: ' + formatCurrency(quote.Amount)
                    }
                    tooltip={renderAcceptTooltipContent()}
                    onClick={() =>
                      setIsAcceptOpen({ open: true, amount: quote.Amount })
                    }
                  />
                  {quote.LenderMaxPossibleAmount && (
                    <ActionsMenuButton
                      isDisabled={isOfferAcceptDisabled}
                      icon={'trending-up'}
                      text={
                        'Accept maximum amount: ' +
                        formatCurrency(quote.LenderMaxPossibleAmount)
                      }
                      tooltip={renderAcceptTooltipContent()}
                      onClick={() =>
                        setIsAcceptOpen({
                          open: true,
                          amount: quote.LenderMaxPossibleAmount ?? 0,
                        })
                      }
                    />
                  )}
                </MenuItem>
                {showEditOffer ? (
                  <ActionsMenuButton
                    icon={'edit'}
                    text={`${quote.Status ? 'Edit' : 'Create'} offer details`}
                    onClick={() => handleEditOffer(!!quote.Status)}
                  />
                ) : null}
                <ActionsMenuButton
                  isDisabled={!canEditAcceptedOffer}
                  icon={'highlight'}
                  text={`Edit accepted offer`}
                  tooltip={renderEditAcceptedOfferTooltipContent()}
                  onClick={() => handleEditAcceptedOffer()}
                />
                {showConfirmCancelByAxo ? (
                  <ConfirmAction
                    confirmationText={'Confirm cancel offer?'}
                    handleConfirmAction={() => {
                      cancelQuoteByAxo.mutate(
                        {
                          quoteID: quote.ID,
                          input: cancelByAxoQuoteInput,
                        },
                        {
                          onSuccess: () => {
                            log(EventCodeACE.OfferCancelledByAxo);
                          },
                        },
                      );
                    }}
                    handleCancelAction={() => setShowConfirmCancelByAxo(false)}
                  />
                ) : (
                  <ActionsMenuButton
                    isDisabled={!quoteCanBeCancelled}
                    icon={'disable'}
                    text={'Cancel by Axo'}
                    tooltip={renderCancelTooltipContent()}
                    onClick={() => setShowConfirmCancelByAxo(true)}
                  />
                )}
                {showConfirmCancelByCustomer ? (
                  <ConfirmAction
                    confirmationText={'Confirm cancel offer?'}
                    handleConfirmAction={() =>
                      cancelQuoteByOnBehalfOfCustomer.mutate(
                        {
                          quoteID: quote.ID,
                          input: cancelOnBehalfOfCustomerQuoteInput,
                        },
                        {
                          onSuccess: () => {
                            log(EventCodeACE.OfferCancelledOnCustomerBehalf);
                          },
                        },
                      )
                    }
                    handleCancelAction={() =>
                      setShowConfirmCancelByCustomer(false)
                    }
                  />
                ) : (
                  <ActionsMenuButton
                    isDisabled={!quoteCanBeCancelled}
                    icon={'disable'}
                    text={'Cancel on Behalf of Customer'}
                    tooltip={renderCancelTooltipContent()}
                    onClick={() => setShowConfirmCancelByCustomer(true)}
                  />
                )}
                {showConfirmCancelRequestByAxo ? (
                  <ConfirmAction
                    confirmationText={'Confirm cancel registration?'}
                    handleConfirmAction={() =>
                      cancelRequest.mutate(undefined, {
                        onSuccess: () => {
                          log(EventCodeACE.RegistrationCancelledByAxo);
                        },
                      })
                    }
                    handleCancelAction={() =>
                      setShowConfirmCancelRequestByAxo(false)
                    }
                  />
                ) : (
                  <ActionsMenuButton
                    isDisabled={!requestCanBeCancelled}
                    icon={'disable'}
                    text={'Cancel registration of loan request'}
                    onClick={() => setShowConfirmCancelRequestByAxo(true)}
                  />
                )}
                {isInQuoteStatusGroup(
                  quote.Status,
                  loan_quote.QuoteStatusGroup.Accepted,
                ) && (
                  <ActionsMenuButton
                    isDisabled={!hasLock.data || quote.IsPlaceholderQuote}
                    icon={'tick-circle'}
                    text={'Mark as active'}
                    onClick={() => activate.mutateAsync()}
                  />
                )}
                <ActionsMenuButton
                  isDisabled={!lenderReference}
                  icon={'duplicate'}
                  text={`Lender Reference ${lenderReference}`}
                  tooltip={tooltipContent.lenderReference.noReference}
                  onClick={() => handleLenderReferenceClick(lenderReference)}
                />
                <ActionsMenuButton
                  isDisabled={!quote.SigningLink}
                  icon={'duplicate'}
                  text={'Copy signing link'}
                  tooltip={tooltipContent.signingLink.noSigningLink}
                  onClick={() =>
                    handleCopySignURLClick(quote.SigningLink ?? '')
                  }
                />
                <ActionsMenuButton
                  isDisabled={!quote.SigningLink}
                  icon={'open-application'}
                  text={'Go to signing page'}
                  tooltip={tooltipContent.signingLink.noSigningLink}
                  onClick={() =>
                    handleGoToSignURLClick(quote.SigningLink ?? '')
                  }
                />
                {quote.LenderStatusURL && (
                  <ActionsMenuButton
                    icon={'open-application'}
                    text={'Go to Bank Status page'}
                    onClick={() =>
                      handleGoToSignURLClick(quote.LenderStatusURL ?? '')
                    }
                  />
                )}
              </>
            </Restricted>
          </Menu>
        }
      >
        <Button aria-label="Actions" disabled={isActionsButtonDisabled}>
          <Icon icon="add-to-artifact" />
        </Button>
      </Popover>
      {data
        .filter((x) => x.LenderID === quote.LenderID)
        .map((x) => {
          const handleClose = () => setOpenMapping(null);
          return (
            <Dialog
              key={x.ID}
              isOpen={openMapping === x.ID}
              onClose={handleClose}
              title={`Loan request mapping: ${formatDatetime(x.CreatedAt)}`}
              icon="git-push"
            >
              <div className={styles.dialogBody}>
                <pre>
                  <code>TODO: extract payload from request here</code>
                </pre>
              </div>
              <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                  <Button onClick={handleClose}>Close</Button>
                </div>
              </div>
            </Dialog>
          );
        })}
      <AcceptDialog
        isOpen={isAcceptOpen.open}
        onClose={() => setIsAcceptOpen({ open: false, amount: 0 })}
        application={application}
        item={quote}
        amount={isAcceptOpen.amount}
        setAmount={(amount) => setIsAcceptOpen({ open: true, amount })}
      />
      {showuldRenderEditAcceptedOffer() ? (
        <AcceptedOfferDialog
          isOpen={isEditAcceptedOfferOpen.open}
          onClose={() => setIsEditAcceptedOfferOpen({ open: false })}
          application={application}
          item={quote}
        />
      ) : null}
    </div>
  );
}
